import React, { useState, useMemo, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  addWeeks,
  subWeeks,
} from "date-fns";

export default function VendorBookingsCalendar({
  bookings = [], // 👈 array of bookings
  onEventClick, // 👈 (bookingId) => void
}) {
  const isMobile = window.innerWidth < 640;

  const [currentDate, setCurrentDate] = useState(new Date());
  const getViewByScreen = () => {
    return window.innerWidth < 640 ? "week" : "month";
  };

  const [view, setView] = useState(getViewByScreen);

  useEffect(() => {
    const handleResize = () => {
      const newView = getViewByScreen();
      setView((prev) => (prev !== newView ? newView : prev));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* =========================
     DATE RANGE
  ========================= */
  const rangeStart =
    view === "month"
      ? startOfWeek(startOfMonth(currentDate))
      : startOfWeek(currentDate);

  const rangeEnd =
    view === "month"
      ? endOfWeek(endOfMonth(currentDate))
      : endOfWeek(currentDate);

  const days = [];
  let day = rangeStart;
  while (day <= rangeEnd) {
    days.push(day);
    day = addDays(day, 1);
  }

  /* =========================
     GROUP BOOKINGS BY DAY
  ========================= */
  const bookingsByDay = useMemo(() => {
    return bookings && typeof bookings === "object" ? bookings : {};
  }, [bookings]);

  const hasAnyBookings = Object.keys(bookingsByDay).length > 0;
  const [openTooltipDate, setOpenTooltipDate] = useState(null);

  const goPrev = () => {
    setCurrentDate(
      view === "week" ? subWeeks(currentDate, 1) : subMonths(currentDate, 1),
    );
  };

  const goNext = () => {
    setCurrentDate(
      view === "week" ? addWeeks(currentDate, 1) : addMonths(currentDate, 1),
    );
  };

  return (
    <div className="border rounded-xl bg-white p-4">
      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-center mb-4">
        {/* MONTH NAV */}
        <div className="flex items-center gap-2">
          <button type="button" onClick={goPrev} className="px-2 text-lg">
            ‹
          </button>

          <div className="font-semibold">
            {format(currentDate, "MMMM yyyy")}
          </div>

          <button type="button" onClick={goNext} className="px-2 text-lg">
            ›
          </button>
        </div>

        {/* VIEW TOGGLE */}
        {/* <div className="flex gap-2">
          {["month", "week"].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`px-3 py-1 rounded-md text-sm border ${
                view === v
                  ? "bg-[#1FADC6] text-white border-[#1FADC6]"
                  : "bg-white text-gray-600 border-gray-300"
              }`}
            >
              {v === "month" ? "Month" : "Week"}
            </button>
          ))}
        </div> */}
      </div>

      {/* ================= WEEK DAYS ================= */}
      <div className="grid grid-cols-7 text-xs font-semibold text-center mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* ================= DAYS GRID ================= */}
      <div
        className={`grid gap-2 ${
          view === "week" ? "grid-cols-1" : "grid-cols-7"
        }`}
      >
        {days.map((d, i) => {
          const dateKey = format(d, "yyyy-MM-dd");
          const dayBookings = bookingsByDay[dateKey] || [];

          return (
            <div
              className={`
    border rounded-md p-2 text-xs
    ${view === "week" ? "min-h-[120px]" : "min-h-[90px]"}
    ${!isSameMonth(d, currentDate) ? "opacity-30" : ""}
  `}
            >
              {/* DATE */}
              <div className="flex justify-between items-center mb-1">
                <div className="font-semibold">{format(d, "EEE")}</div>
                <div className="font-semibold">{format(d, "d")}</div>
              </div>

              {/* BOOKINGS (MAX 2) */}
              {dayBookings.slice(0, 2).map((b) => (
                <div
                  key={b.bookingId}
                  onClick={() => onEventClick?.(b.bookingId)}
                  className="
      bg-[#E6F7FA]
      text-[#035D67]
      rounded-md
      px-2 py-1
      mb-1
      cursor-pointer
      hover:bg-[#D2F0F4]
    "
                >
                  <div className="font-semibold truncate">{b.experience}</div>

                  <div className="text-[11px] text-gray-600">
                    ⏰ {b.timeSlot} • 👥 {b.guestCount} guest
                    {b.guestCount !== 1 ? "s" : ""}
                  </div>
                </div>
              ))}

              {/* + MORE */}
              {dayBookings.length > 2 && (
                <div className="relative inline-block">
                  {/* + MORE */}
                  <div
                    className="text-[11px] text-[#1FADC6] cursor-pointer select-none"
                    onClick={() =>
                      setOpenTooltipDate(
                        openTooltipDate === dateKey ? null : dateKey,
                      )
                    }
                  >
                    +{dayBookings.length - 2} more
                  </div>

                  {/* TOOLTIP */}
                  {openTooltipDate === dateKey && (
                    <div
                      className="
       absolute z-50
  left-0 top-full mt-1
  w-56
  bg-white border border-gray-200
  rounded-md shadow-lg
  p-2
      "
                    >
                      <div className="space-y-2">
                        {dayBookings.map((b) => (
                          <div
                            key={b.bookingId}
                            onClick={() => onEventClick?.(b.bookingId)}
                            className="
              cursor-pointer
              border-b last:border-b-0
              pb-1 last:pb-0
              hover:bg-gray-50
              rounded px-1
            "
                          >
                            <div className="font-semibold truncate">
                              {b.experience}
                            </div>
                            <div className="text-[11px] text-gray-600">
                              ⏰ {b.timeSlot} • 👥 {b.guestCount} guest
                              {b.guestCount !== 1 ? "s" : ""}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ================= EMPTY STATE ================= */}
      {!hasAnyBookings && (
        <div className="text-center text-gray-500 text-sm py-10">
          No bookings scheduled in this range.
        </div>
      )}
    </div>
  );
}
